import PropTypes from 'prop-types';
import flow from 'lodash/flow';
import get from 'lodash/get';

import { playbackStatusIcons } from 'src/common/utils/playbackStatusIconMap';
import withSyncedPlayerBehavior from 'src/common/decorators/withSyncedPlayerBehavior';
import withMouseHover from 'src/common/decorators/hovers/withMouseHover';
import cssVariables from 'src/common/styles/variables';
import GuideItemLink from '../../../shared/GuideItemLink';
import LockedTile from '../../../shared/LockedTile';
import isLocked from '../../../../../utils/guideItem/isLocked';

import css from '../../../shared/guide-item.module.scss';
import PlayButton from './PlayButton';
import playSectionCss from './play-section.module.scss';

/**
 * This component renders a detail section of that contains all the information
 * needed to play a station/podcast.  It includes the proper play button
 *
 * @param {object} guideItem - The guideItem that pertains to this particular station/podcast
 * @param {string} syncedPlayerStatus - The current status of the player
 * @param {boolean} canScrub - Whether or not the stream can be scrubbed
 * @param {boolean} isHovering - Decides what color to display
 * @param {function} handleMouseEnter - Callback for mouseEnter event handler
 * @param {function} handleMouseLeave - Callback for mouseLeave event handler
 * @param {function} handlePlayButtonClick - Callback for play button click event handler
 * @param {string} playerStatus - The current status of the player
 * @param {string} externalHtmlPlayerUrl - The URL to the stream to be played in a popout
 * @param {string} tunedGuideId - Currently playing item in the player
 */
export const PlayableSection = ({
  guideItem,
  syncedPlayerStatus,
  canScrub,
  isHovering,
  handleMouseEnter,
  handleMouseLeave,
  handlePlayButtonClick,
  playerStatus,
  externalHtmlPlayerUrl,
  tunedGuideId,
}) => {
  const hoverColor = isHovering
    ? cssVariables['--primary-color-1-hover']
    : cssVariables['--secondary-color-5'];

  const titleStyle = {
    color: hoverColor,
  };

  const playbackStatus = get(guideItem, 'properties.playbackStatus');
  const isNewPlaybackItem =
    playbackStatus &&
    playbackStatus.statusIcon === playbackStatusIcons.newPlaybackItem;

  /**
   * Construct the child elements of the playable section with the appropriate callbacks
   */
  const children = (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div className={playSectionCss.playSectionLeftContent}>
        <div className={playSectionCss.playSectionButtonWrapper}>
          <PlayButton
            externalHtmlPlayerUrl={externalHtmlPlayerUrl}
            playerStatus={playerStatus}
            playbackStatus={playbackStatus}
            syncedPlayerStatus={syncedPlayerStatus}
            canScrub={canScrub}
            color={hoverColor}
            guideItem={guideItem}
            tunedGuideId={tunedGuideId}
          />
        </div>
      </div>
      <div className={playSectionCss.playSectionMainContent}>
        <h2
          className={css.guideItemTitleSingleLine}
          style={titleStyle}
          data-testid="statusTitle"
        >
          {guideItem.title}
          {isNewPlaybackItem && (
            <div
              data-testid="newPlayableItemIndicator"
              className={css.newPlayableItemIndicator}
            />
          )}
        </h2>
        {guideItem.subtitle && (
          <div className={css.guideItemSubtitle} data-testid="statusSubtitle">
            {guideItem.subtitle}
          </div>
        )}
      </div>
    </div>
  );

  /* If should show the upsell dialog */
  if (isLocked(guideItem)) {
    return (
      <LockedTile
        dataTestId="statusPlayerLockedAction"
        guideItem={guideItem}
        className={playSectionCss.playSectionContainer}
      >
        {children}
      </LockedTile>
    );
  }

  /**
   * Render the component
   */
  return (
    <GuideItemLink
      dataTestId="statusPlayerAction"
      className={playSectionCss.playSectionContainer}
      onClick={handlePlayButtonClick}
      guideItem={guideItem}
    >
      {children}
    </GuideItemLink>
  );
};

PlayableSection.propTypes = {
  guideItem: PropTypes.object.isRequired,
  syncedPlayerStatus: PropTypes.string.isRequired,
  canScrub: PropTypes.bool,
  isHovering: PropTypes.bool.isRequired,
  handleMouseEnter: PropTypes.func.isRequired,
  handleMouseLeave: PropTypes.func.isRequired,
  handlePlayButtonClick: PropTypes.func.isRequired,
  playerStatus: PropTypes.string,
  externalHtmlPlayerUrl: PropTypes.string,
  tunedGuideId: PropTypes.string,
};

export default flow(withMouseHover, withSyncedPlayerBehavior)(PlayableSection);
